import moment from 'moment-timezone';

// Integer scaling: *_x10 values are tenths, *_x100 values are hundredths.
export const formatScaled = (value, scale) => {
  if (value === null || value === undefined || value === '') return '';
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  return (n / scale).toFixed(scale === 10 ? 1 : 2);
};

export const RANKED_COLUMNS = [
  {
    key: 'rank',
    label: 'Rank',
    tip: 'Final position after sorting by fit, 555 decile, retention decile, then a seeded random tiebreak.'
  },
  { key: 'teacher', label: 'Teacher', tip: 'Teacher name and id.' },
  {
    key: 'goals',
    label: 'Goals matched',
    tip: 'matched_goals / total_goals. Health goals auto-match when the teacher has the required therapy/prenatal flag; general goals match via teacher goal mapping.'
  },
  {
    key: 'goal_score_x10',
    label: 'Goal score',
    tip: 'goal_score_x10 = floor(matched × 90 / total). Integer tenths, 0..90 (i.e. 0.0..9.0). 0 when the student has no goals.'
  },
  {
    key: 'days_available',
    label: 'Days / 14',
    tip: 'Distinct days in the next 14 (day 0..13) on which the teacher is free at the same wall-clock time in the student timezone.'
  },
  {
    key: 'repeat_available',
    label: 'Repeat',
    tip: 'Teacher offers this slot weekly. Adds 10 (1.0) to the availability score.'
  },
  {
    key: 'avail_score_x10',
    label: 'Avail score',
    tip: 'avail_score_x10 = floor(days × 80 / 14) + (repeat ? 10 : 0). Integer tenths, 0..90 (0.0..9.0).'
  },
  {
    key: 'fit_score',
    label: 'Fit',
    tip: 'fit_score = goal_score_x10 × 6 + avail_score_x10 × 4. Integer 0..900 (equals Goal×60 + Avail×40 on one-decimal scores). Primary sort key.'
  },
  {
    key: 'finished_trials',
    label: 'Finished trials',
    tip: 'Finished 1-on-1 YOGA trial sessions. Fewer than 3 means new teacher: effective points forced to 20.'
  },
  {
    key: 'points_balance',
    label: '555 balance',
    tip: 'Raw teacher_trial_points.current_balance (Conversion Points). No row means 0.'
  },
  {
    key: 'effective_points_x100',
    label: 'Eff. points',
    tip: 'effective_points_x100: 20.00 if finished trials < 3, else the balance. Integer hundredths (2350 = 23.50).'
  },
  {
    key: 'points_decile',
    label: '555 decile',
    tip: 'Decile 0..9 of effective points among Stage-0 survivors (ASC, tiebreak teacher_id ASC; floor(pos × 10 / N)). Second sort key.'
  },
  {
    key: 'retention_score_x100',
    label: 'Retention',
    tip: 'retention_score_x100 = retention points × 100 / unique students, from the nightly batch. Integer hundredths. AVG badge = platform average used (new teacher / no data).'
  },
  {
    key: 'retention_decile',
    label: 'Ret. decile',
    tip: 'Decile 0..9 of retention score among Stage-0 survivors. Third sort key.'
  },
  {
    key: 'decided_by',
    label: 'Decided by',
    tip: 'Which sort key separated this teacher from the next one: fit, points, retention, random, or last.'
  },
  {
    key: 'shown_status',
    label: 'Shown status',
    tip: 'Rank 1..3 shown, 4..6 see_more, else see_all. Other server values (e.g. excluded) are shown as-is.'
  },
  { key: 'reason', label: 'Reason', tip: 'Reason text, generated for the top max(6, N) only.' }
];

export const GENDER_PREF_OPTIONS = ['FEMALE', 'MALE', 'NO_PREFERENCE'];
export const STUDENT_GENDER_OPTIONS = ['FEMALE', 'MALE'];
export const ETHNICITY_OPTIONS = ['WESTERN', 'INDIAN', 'NO_MATCH'];

export const EMPTY_OVERRIDES = {
  goals: null,
  teacher_gender_preference: '',
  student_gender: '',
  student_ethnicity: '',
  country_iso: '',
  is_referral_student: '',
  is_prenatal_student: '',
  disable_thin_pool_relaxation: false
};

// Builds the request overrides object; empty fields are omitted so the server uses student data.
export const buildOverrides = (o) => {
  const out = {};
  if (Array.isArray(o.goals)) out.goals = o.goals;
  if (o.teacher_gender_preference) out.teacher_gender_preference = o.teacher_gender_preference;
  if (o.student_gender) out.student_gender = o.student_gender;
  if (o.student_ethnicity) out.student_ethnicity = o.student_ethnicity;
  if (o.country_iso.trim()) out.country_iso = o.country_iso.trim().toUpperCase();
  if (o.is_referral_student) out.is_referral_student = o.is_referral_student === 'true';
  if (o.is_prenatal_student) out.is_prenatal_student = o.is_prenatal_student === 'true';
  if (o.disable_thin_pool_relaxation) out.disable_thin_pool_relaxation = true;
  return out;
};

// Meta goals may arrive as strings or student_goal-like objects.
export const goalName = (g) => {
  if (typeof g === 'string') return g;
  if (!g) return '';
  return g.display_name || g.goal_display_name || g.name || '';
};

export const allFilterResults = (t) => [
  ...((t.phase_a && t.phase_a.results) || []).map((r) => ({ ...r, phase: 'A' })),
  ...((t.phase_b && t.phase_b.results) || []).map((r) => ({ ...r, phase: 'B' }))
];

export const teacherFailed = (t) =>
  (t.phase_a && t.phase_a.passed === false) || (t.phase_b && t.phase_b.passed === false);

const yesNo = (v) => (v ? 'yes' : 'no');

export const rankedCsvRows = (ranked) =>
  (ranked || []).map((t) => ({
    rank: t.rank,
    teacher_id: t.teacher_id,
    teacher_uuid: t.teacher_uuid,
    name: t.name,
    gender: t.gender,
    goals_matched: (t.goals_matched || []).join(', '),
    matched_goals: t.matched_goals,
    total_goals: t.total_goals,
    goal_score_x10: t.goal_score_x10,
    days_available: t.days_available,
    repeat_available: yesNo(t.repeat_available),
    avail_score_x10: t.avail_score_x10,
    fit_score: t.fit_score,
    finished_trials: t.finished_trials,
    points_balance: t.points_balance,
    effective_points_x100: t.effective_points_x100,
    points_decile: t.points_decile,
    retention_score_x100: t.retention_score_x100,
    retention_is_platform_avg: yesNo(t.retention_is_platform_avg),
    retention_decile: t.retention_decile,
    decided_by: t.decided_by,
    shown_status: t.shown_status,
    reason: t.reason
  }));

const filterSummary = (t) =>
  allFilterResults(t)
    .map(
      (r) =>
        `${r.phase}:${r.filter}=${r.passed ? 'pass' : 'FAIL'}${r.reason ? ` (${r.reason})` : ''}`
    )
    .join('; ');

export const poolCsvRows = (pool) =>
  (pool || []).map((t) => ({
    teacher_id: t.teacher_id,
    teacher_uuid: t.teacher_uuid,
    name: t.name,
    gender: t.gender,
    phase_a_passed: t.phase_a ? yesNo(t.phase_a.passed) : '',
    phase_b_passed: t.phase_b ? yesNo(t.phase_b.passed) : '',
    rank: t.rank || '',
    fit_score: t.fit_score,
    filters: filterSummary(t)
  }));

export const SLOT_ALIGN_ERROR = 'Slot must start on a :00 or :30 UTC boundary.';

// Parses a student-local date + HH:mm in tz; null when unparseable.
export const parseSlot = (date, time, tz) => {
  const slot = moment.tz(`${date} ${time}`, 'YYYY-MM-DD HH:mm', true, tz || 'UTC');
  return slot.isValid() ? slot : null;
};

// Slots must align to a 30-minute boundary in UTC epoch seconds.
export const slotAlignError = (date, time, tz) => {
  if (!date || !time) return '';
  const slot = parseSlot(date, time, tz);
  if (!slot) return '';
  return slot.unix() % 1800 === 0 ? '' : SLOT_ALIGN_ERROR;
};
